import { ContentCopy as CopyIcon } from '@mui/icons-material'
import { Alert, Box, Button, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useDataProvider, useNotify, useTranslate } from 'react-admin'
import { PredecessorPet, usePossiblePredecessors } from '../hooks/usePossiblePredecessors'

/**
 * Raw alert-rule as returned by the backend. Field names may be camelCase or
 * snake_case depending on the serializer, so we read both — same defensive
 * mapping the AlertRulesSection loader uses.
 */
type RawRule = Record<string, any>

export interface CopiedRule {
  pet: number
  ruleType: string
  isActive: boolean
  thresholdUnit: 'mmol/L' | 'mg/dL'
  triggerCount: number
  emailNotificationEnabled?: boolean
  smsNotificationEnabled?: boolean
  criticalThreshold?: number
  warningThreshold?: number
  hyperglycemiaThreshold?: number
  severeHyperglycemiaThreshold?: number
  somogyiReboundThreshold?: number
  dawnElevationThreshold?: number
  rapidDropThreshold?: number
  postprandialSpikeThreshold?: number
  severeSpikeThreshold?: number
  postprandialWindowHours?: number
  preMealWindowHours?: number
  patternAnalysisWindow?: number
  notes?: string
}

interface AlertThresholdCopyBannerProps {
  /** The pet currently being edited (copy destination). */
  petId: number
  /**
   * Whether this pet already has alert rules persisted in the backend. The
   * banner only appears when it does NOT — i.e. a freshly-created profile that
   * would otherwise force the vet to re-enter thresholds from scratch.
   */
  currentHasSavedRules: boolean
  /**
   * Called with rules mapped onto the current pet. The parent prefills the
   * editor and marks it dirty — nothing is saved until the vet reviews and
   * clicks Save. This is intentional: clinical thresholds must pass human eyes.
   */
  onCopy: (rules: CopiedRule[]) => void
}

const num = (...vals: any[]): number | undefined => {
  for (const v of vals) {
    if (v !== undefined && v !== null && !Number.isNaN(Number(v))) return Number(v)
  }
  return undefined
}

const mapRule = (rule: RawRule, targetPetId: number): CopiedRule => ({
  // Drop the source id so a Save creates fresh rows owned by the target pet.
  pet: targetPetId,
  ruleType: rule.ruleType || rule.rule_type || rule.type,
  isActive: rule.isActive ?? rule.is_active ?? true,
  thresholdUnit: rule.thresholdUnit || rule.threshold_unit || 'mmol/L',
  triggerCount: 0,
  emailNotificationEnabled:
    rule.emailNotificationEnabled ?? rule.email_notification_enabled ?? true,
  smsNotificationEnabled: rule.smsNotificationEnabled ?? rule.sms_notification_enabled ?? false,
  criticalThreshold: num(rule.criticalThreshold, rule.critical_threshold),
  warningThreshold: num(rule.warningThreshold, rule.warning_threshold),
  hyperglycemiaThreshold: num(rule.hyperglycemiaThreshold, rule.hyperglycemia_threshold),
  severeHyperglycemiaThreshold: num(
    rule.severeHyperglycemiaThreshold,
    rule.severe_hyperglycemia_threshold,
  ),
  somogyiReboundThreshold: num(rule.somogyiReboundThreshold, rule.somogyi_rebound_threshold),
  dawnElevationThreshold: num(rule.dawnElevationThreshold, rule.dawn_elevation_threshold),
  rapidDropThreshold: num(rule.rapidDropThreshold, rule.rapid_drop_threshold),
  postprandialSpikeThreshold: num(
    rule.postprandialSpikeThreshold,
    rule.postprandial_spike_threshold,
  ),
  severeSpikeThreshold: num(rule.severeSpikeThreshold, rule.severe_spike_threshold),
  postprandialWindowHours: num(rule.postprandialWindowHours, rule.postprandial_window_hours),
  preMealWindowHours: num(rule.preMealWindowHours, rule.pre_meal_window_hours),
  patternAnalysisWindow: num(rule.patternAnalysisWindow, rule.pattern_analysis_window),
  notes: rule.notes || rule.description || '',
})

/**
 * Non-blocking banner offering to copy alert thresholds from a prior pet
 * profile (the same animal, before a new sensor spun up a fresh record). It is
 * the human-confirmed half of the "thresholds reset every sensor" fix — see
 * usePossiblePredecessors and ada-core Pets/possiblePredecessors.ts.
 */
export const AlertThresholdCopyBanner: React.FC<AlertThresholdCopyBannerProps> = ({
  petId,
  currentHasSavedRules,
  onCopy,
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const dataProvider = useDataProvider()
  const { predecessors } = usePossiblePredecessors(petId)
  const [dismissed, setDismissed] = useState(false)
  const [copying, setCopying] = useState(false)

  // Best source = first predecessor that actually carries thresholds.
  // usePossiblePredecessors already sorts rules-bearing pets first.
  const source: PredecessorPet | undefined = predecessors.find((p) => p.hasAlertRules)

  if (currentHasSavedRules || dismissed || !source) {
    return null
  }

  const handleCopy = async () => {
    setCopying(true)
    try {
      const { data } = await dataProvider.getList('alert-rules', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'id', order: 'ASC' },
        filter: { pet: source.id },
      })
      const mapped = (data || [])
        .map((r: RawRule) => mapRule(r, petId))
        .filter((r: CopiedRule) => !!r.ruleType)
      if (mapped.length === 0) {
        notify(translate('pos.alert_rules.copy.none_found'), { type: 'warning' })
        return
      }
      onCopy(mapped)
      notify(translate('pos.alert_rules.copy.copied'), { type: 'info' })
      setDismissed(true)
    } catch {
      notify(translate('pos.alert_rules.copy.failed'), { type: 'error' })
    } finally {
      setCopying(false)
    }
  }

  return (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      onClose={() => setDismissed(true)}
      action={
        <Button
          color="inherit"
          size="small"
          startIcon={<CopyIcon />}
          disabled={copying}
          onClick={handleCopy}
          data-testid="copy-thresholds-btn"
        >
          {translate('pos.alert_rules.copy.action')}
        </Button>
      }
    >
      <Typography variant="body2">
        {translate('pos.alert_rules.copy.prompt', {
          name: source.name,
          count: source.alertRuleCount,
        })}
      </Typography>
      <Box component="span" sx={{ display: 'block', fontSize: '0.8rem', opacity: 0.85, mt: 0.5 }}>
        {translate('pos.alert_rules.copy.review_hint')}
      </Box>
    </Alert>
  )
}

export default AlertThresholdCopyBanner
